import { createSignal, onMount, Show, For } from "solid-js";
import * as d3 from "d3";
import DOMPurify from "dompurify";

import { getData, deleteData } from "../../../../utils/global";
import { isMeaningfulNoteSection, notesHaveMeaningfulContent } from "../../../../utils/notesPageContent";
import { setSidebarMenuRefreshTrigger, sidebarMenuRefreshTrigger } from "../../../../store/globalStore";
import { debug, error as logError, warn } from "../../../../utils/console";
import { persistantStore } from "../../../../store/persistantStore";
import { apiEndpoints } from "@config/env";
import { logPageLoad } from "../../../../utils/logging";
import Loading from "../../../../components/utilities/Loading";

const { selectedClassName, selectedProjectId, selectedDatasetId } = persistantStore;

/** Dataset object from API: may be string, or object with text/json */
type DatasetObject = string | { text?: string; json?: string | unknown } | null;

/** Configuration table row */
interface ConfigurationRow {
  platform?: string;
  mast?: string;
  foil?: string;
  rudder?: string;
}

/** Crew rotation row */
interface CrewRotation {
  name?: string;
  helmsman?: string;
  wing_trim?: string;
  flight?: string;
  strategy?: string;
  grind_aft?: string;
  grind_fwd?: string;
}

/** Stats table cell for D3-colored rows */
interface StatsTableCell {
  value: string | number;
  class: string;
  isFirst: boolean;
}

/** Stats table result from buildStatsTableData */
interface StatsTableData {
  headers: (string | number)[];
  rows: StatsTableCell[][];
}

interface ManeuverCounts {
  tacks: number;
  gybes: number;
  roundups: number;
  bearaways: number;
}

interface PointOfSailSec {
  upwind: number;
  reaching: number;
  downwind: number;
}

interface ModeSec {
  foiling: number;
  displacement: number;
}

interface SegmentSummary {
  training_sec: number;
  racing_sec: number;
  top_bsp_kts: number;
  tws_avg_kts: number;
  twd_avg_deg: number;
  distance_nm: number;
  total_hours_sec?: number;
  maneuvers: ManeuverCounts;
  point_of_sail_sec: PointOfSailSec;
  mode_sec: ModeSec;
}

interface BreakdownTotals {
  durationSec: number;
  top_bsp_kts: number;
  tws_avg_kts: number;
  twd_avg_deg: number;
  distance_nm: number;
  maneuvers: ManeuverCounts;
  mode_sec: ModeSec;
  point_of_sail_sec: PointOfSailSec;
}

interface RaceSummary extends SegmentSummary {
  race_number: number;
  label?: string;
}

interface ComputedSummary {
  schema_version: number;
  day: SegmentSummary;
  races: RaceSummary[];
}

export default function NotesPage() {
  // State for dataset objects
  const [introduction, setIntroduction] = createSignal<DatasetObject>(null);
  const [notes, setNotes] = createSignal<DatasetObject>(null);
  const [dayTypeNotes, setDayTypeNotes] = createSignal<DatasetObject>(null);
  const [technique, setTechnique] = createSignal<DatasetObject>(null);
  const [howToWin, setHowToWin] = createSignal<DatasetObject>(null);
  const [configuration, setConfiguration] = createSignal<ConfigurationRow | null>(null);
  const [crew, setCrew] = createSignal<CrewRotation | CrewRotation[] | null>(null);
  const [maximums, setMaximums] = createSignal<Record<string, string | number> | null>(null);
  const [usage, _setUsage] = createSignal<Record<string, string | number> | null>(null);
  const [stats, setStats] = createSignal<DatasetObject>(null);
  const [computedSummary, setComputedSummary] = createSignal<ComputedSummary | null>(null);

  // State for dataset metadata (setters used; accessors reserved for future display)
  const [_datasetDate, setDatasetDate] = createSignal("");
  const [_reportName, setReportName] = createSignal("");
  const [_className, setClassName] = createSignal("");

  // Loading and error states
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [hasContent, setHasContent] = createSignal(false);
  /** Dataset table description. It is not a note, even when copied into the summary. */
  const [datasetDescription, setDatasetDescription] = createSignal("");

  // Helper function to fetch a dataset object
  const fetchDatasetObject = async (objectName: string): Promise<DatasetObject> => {
    try {
      const controller = new AbortController();
      const response = await getData(
        `${apiEndpoints.app.datasets}/object?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&dataset_id=${encodeURIComponent(selectedDatasetId())}&parent_name=dataset&object_name=${encodeURIComponent(objectName)}`,
        controller.signal
      );
      
      if (response?.success && response?.data != null) {
        return response.data as DatasetObject;
      }
      return null;
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== "AbortError") {
        logError(`Error fetching dataset object ${objectName}:`, err);
      }
      return null;
    }
  };

  // Helper function to sanitize HTML
  const sanitizeHTML = (html: string | null | undefined): string => {
    if (html == null || html === "") return "";
    return DOMPurify.sanitize(html);
  };

  // Helper function to check if variable is array
  const isArray = (variable: unknown): variable is unknown[] => {
    return Array.isArray(variable);
  };

  // Helper function to get text content from object
  const getText = (obj: DatasetObject): string => {
    if (obj == null) return "";
    try {
      if (typeof obj === "string") return obj;
      const o = obj as { text?: string; json?: string };
      if (o.text) return o.text;
      if (o.json != null && typeof o.json === "string") return o.json;
      return "";
    } catch {
      return "";
    }
  };

  const parseSegmentSummary = (raw: unknown): SegmentSummary | null => {
    if (raw == null || typeof raw !== "object") return null;
    const seg = raw as Record<string, unknown>;
    const maneuversRaw = seg.maneuvers as Record<string, unknown> | undefined;
    const posRaw = seg.point_of_sail_sec as Record<string, unknown> | undefined;
    const modeRaw = seg.mode_sec as Record<string, unknown> | undefined;
    return {
      training_sec: Number(seg.training_sec) || 0,
      racing_sec: Number(seg.racing_sec) || 0,
      top_bsp_kts: Number(seg.top_bsp_kts) || 0,
      tws_avg_kts: Number(seg.tws_avg_kts) || 0,
      twd_avg_deg: Number(seg.twd_avg_deg) || 0,
      distance_nm: Number(seg.distance_nm) || 0,
      maneuvers: {
        tacks: Number(maneuversRaw?.tacks) || 0,
        gybes: Number(maneuversRaw?.gybes) || 0,
        roundups: Number(maneuversRaw?.roundups) || 0,
        bearaways: Number(maneuversRaw?.bearaways) || 0,
      },
      point_of_sail_sec: {
        upwind: Number(posRaw?.upwind) || 0,
        reaching: Number(posRaw?.reaching) || 0,
        downwind: Number(posRaw?.downwind) || 0,
      },
      mode_sec: {
        foiling: Number(modeRaw?.foiling) || 0,
        displacement: Number(modeRaw?.displacement) || 0,
      },
      total_hours_sec: Number(seg.total_hours_sec) || 0,
    };
  };

  const ensureTrainingRow = (day: SegmentSummary, races: RaceSummary[]): RaceSummary[] => {
    const hasTraining = races.some((r) => r.label === "Training");
    if (hasTraining || day.training_sec <= 0) return races;
    return [
      {
        race_number: 0,
        label: "Training",
        training_sec: day.training_sec,
        racing_sec: 0,
        top_bsp_kts: day.top_bsp_kts,
        tws_avg_kts: day.tws_avg_kts,
        twd_avg_deg: day.twd_avg_deg,
        distance_nm: 0,
        maneuvers: { tacks: 0, gybes: 0, roundups: 0, bearaways: 0 },
        point_of_sail_sec: { ...day.point_of_sail_sec },
        mode_sec: { ...day.mode_sec },
      },
      ...races,
    ];
  };

  const reconcileDayFromBreakdown = (day: SegmentSummary, races: RaceSummary[]): SegmentSummary => {
    if (races.length === 0) return day;
    const totalDistance = races.reduce((sum, r) => sum + (r.distance_nm || 0), 0);
    const totalDuration = races.reduce(
      (sum, r) => sum + (r.label === "Training" ? r.training_sec : r.racing_sec),
      0,
    );
    const topBsp = Math.max(...races.map((r) => r.top_bsp_kts || 0), day.top_bsp_kts || 0);
    return {
      ...day,
      distance_nm: totalDistance > 0 ? totalDistance : day.distance_nm,
      total_hours_sec: totalDuration > 0 ? totalDuration : day.training_sec + day.racing_sec,
      top_bsp_kts: topBsp,
    };
  };

  const parseComputedSummary = (obj: DatasetObject): ComputedSummary | null => {
    if (obj == null) return null;
    try {
      let data: unknown = obj;
      if (typeof obj === "string") {
        data = JSON.parse(obj);
      } else {
        const o = obj as { json?: string | Record<string, unknown>; text?: string | Record<string, unknown> };
        if (o.json != null) {
          data = typeof o.json === "string" ? JSON.parse(o.json) : o.json;
        } else if (o.text != null) {
          data = typeof o.text === "string" ? JSON.parse(o.text) : o.text;
        }
      }
      if (data == null || typeof data !== "object") return null;
      const root = data as Record<string, unknown>;
      const day = parseSegmentSummary(root.day);
      if (!day) return null;
      const racesRaw = Array.isArray(root.races) ? root.races : [];
      const races: RaceSummary[] = [];
      for (const race of racesRaw) {
        const seg = parseSegmentSummary(race);
        if (!seg) continue;
        const raw = race as Record<string, unknown>;
        const labelRaw = typeof raw.label === "string" ? raw.label : undefined;
        const raceNum = Number(raw.race_number);
        const isTraining = labelRaw?.toLowerCase() === "training" || raceNum === 0;
        if (!isTraining && (!raceNum || raceNum <= 0)) continue;
        races.push({
          race_number: isTraining ? 0 : raceNum,
          label: isTraining ? "Training" : labelRaw,
          ...seg,
        });
      }
      const withTraining = ensureTrainingRow(day, races);
      withTraining.sort((a, b) => {
        if (a.label === "Training") return -1;
        if (b.label === "Training") return 1;
        return a.race_number - b.race_number;
      });
      const reconciledDay = reconcileDayFromBreakdown(day, withTraining);
      return {
        schema_version: Number(root.schema_version) || 1,
        day: reconciledDay,
        races: withTraining,
      };
    } catch (err: unknown) {
      logError("Error parsing computed summary:", err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds || seconds <= 0) return "—";
    const totalMin = Math.round(seconds / 60);
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatNumber = (value: number, decimals = 1): string => {
    if (value == null || Number.isNaN(value)) return "—";
    return value.toFixed(decimals);
  };

  const parseConfiguration = (obj: DatasetObject): ConfigurationRow | null => {
    if (obj == null) return null;
    try {
      let data: unknown = obj;
      if (typeof obj === "string") {
        data = JSON.parse(obj);
      } else {
        const o = obj as { json?: string | Record<string, unknown>; text?: string | Record<string, unknown> };
        if (o.json != null) {
          data = typeof o.json === "string" ? JSON.parse(o.json) : o.json;
        } else if (o.text != null) {
          data = typeof o.text === "string" ? JSON.parse(o.text) : o.text;
        }
      }
      if (data == null || typeof data !== "object" || Array.isArray(data)) return null;
      const cfg = data as Record<string, unknown>;
      const pick = (...keys: string[]): string => {
        for (const key of keys) {
          const direct = cfg[key];
          if (direct != null && String(direct).trim() !== "") return String(direct).trim();
          const upper = key.toUpperCase();
          const upperVal = cfg[upper];
          if (upperVal != null && String(upperVal).trim() !== "") return String(upperVal).trim();
        }
        return "";
      };
      const row: ConfigurationRow = {
        platform: pick("platform"),
        mast: pick("mast", "MAST"),
        foil: pick("foil", "FOIL"),
        rudder: pick("rudder", "RUDDER"),
      };
      if (!row.platform && !row.mast && !row.foil && !row.rudder) return null;
      return row;
    } catch (err: unknown) {
      logError("Error parsing configuration:", err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  };

  const formatTotalHours = (seconds: number): string => {
    if (!seconds || seconds <= 0) return "—";
    return `${(seconds / 3600).toFixed(1)} h`;
  };

  const dayTotalHoursSec = (day: SegmentSummary): number =>
    day.total_hours_sec && day.total_hours_sec > 0
      ? day.total_hours_sec
      : day.training_sec + day.racing_sec;

  const weightedCircularTwd = (rows: RaceSummary[], durationFn: (r: RaceSummary) => number): number => {
    let sinSum = 0;
    let cosSum = 0;
    let totalWeight = 0;
    for (const row of rows) {
      const weight = durationFn(row);
      if (weight <= 0 || !row.twd_avg_deg) continue;
      const rad = (row.twd_avg_deg * Math.PI) / 180;
      sinSum += Math.sin(rad) * weight;
      cosSum += Math.cos(rad) * weight;
      totalWeight += weight;
    }
    if (totalWeight <= 0) return 0;
    const deg = (Math.atan2(sinSum, cosSum) * 180) / Math.PI;
    return ((deg % 360) + 360) % 360;
  };

  const buildBreakdownTotals = (rows: RaceSummary[]): BreakdownTotals | null => {
    if (rows.length === 0) return null;
    let durationSec = 0;
    let distanceNm = 0;
    let topBsp = 0;
    let twsWeighted = 0;
    let twsWeight = 0;
    const maneuvers: ManeuverCounts = { tacks: 0, gybes: 0, roundups: 0, bearaways: 0 };
    const modeSec: ModeSec = { foiling: 0, displacement: 0 };
    const posSec: PointOfSailSec = { upwind: 0, reaching: 0, downwind: 0 };

    for (const row of rows) {
      const dur = raceDurationSec(row);
      durationSec += dur;
      distanceNm += row.distance_nm || 0;
      topBsp = Math.max(topBsp, row.top_bsp_kts || 0);
      if (dur > 0 && row.tws_avg_kts) {
        twsWeighted += row.tws_avg_kts * dur;
        twsWeight += dur;
      }
      maneuvers.tacks += row.maneuvers.tacks;
      maneuvers.gybes += row.maneuvers.gybes;
      maneuvers.roundups += row.maneuvers.roundups;
      maneuvers.bearaways += row.maneuvers.bearaways;
      modeSec.foiling += row.mode_sec.foiling;
      modeSec.displacement += row.mode_sec.displacement;
      posSec.upwind += row.point_of_sail_sec.upwind;
      posSec.reaching += row.point_of_sail_sec.reaching;
      posSec.downwind += row.point_of_sail_sec.downwind;
    }

    return {
      durationSec,
      top_bsp_kts: topBsp,
      tws_avg_kts: twsWeight > 0 ? twsWeighted / twsWeight : 0,
      twd_avg_deg: weightedCircularTwd(rows, raceDurationSec),
      distance_nm: distanceNm,
      maneuvers,
      mode_sec: modeSec,
      point_of_sail_sec: posSec,
    };
  };

  const raceDisplayName = (race: RaceSummary): string =>
    race.label === "Training" ? "Training" : `R${race.race_number}`;

  const raceDurationSec = (race: RaceSummary): number =>
    race.label === "Training" ? race.training_sec : race.racing_sec;

  // Build stats table data
  const buildStatsTableData = (statsObj: DatasetObject): StatsTableData | null => {
    if (statsObj == null) return null;
    
    try {
      let data: Record<string, string | number>[];
      if (typeof statsObj === "string") {
        data = JSON.parse(statsObj) as Record<string, string | number>[];
      } else {
        const o = statsObj as { json?: string | Record<string, string | number>[]; text?: string | Record<string, string | number>[] };
        if (o.json != null) {
          data = typeof o.json === "string" ? (JSON.parse(o.json) as Record<string, string | number>[]) : o.json as Record<string, string | number>[];
        } else if (o.text != null) {
          data = typeof o.text === "string" ? (JSON.parse(o.text) as Record<string, string | number>[]) : o.text as Record<string, string | number>[];
        } else if (Array.isArray(statsObj)) {
          data = statsObj as Record<string, string | number>[];
        } else {
          data = (statsObj as unknown) as Record<string, string | number>[];
        }
      }
      
      if (!Array.isArray(data) || data.length === 0) return null;

      const twsScale = d3.scaleLinear().range([0, 6]).domain([0, 25]);
      const swhScale = d3.scaleLinear().range([0, 6]).domain([0, 1]);
      const distScale = d3.scaleLinear().range([0, 6]).domain([0, 35]);

      const headers: (string | number)[] = [];
      if (data[0]) {
        for (const key in data[0]) {
          headers.push(data[0][key]);
        }
      }

      const rows: StatsTableCell[][] = [];
      for (let i = 1; i < data.length; i++) {
        const row: StatsTableCell[] = [];
        let colIndex = 0;
        for (const key in data[i]) {
          const value = data[i][key];
          const numVal = typeof value === "number" ? value : parseFloat(String(value)) || 0;
          let cellClass = "centered";
          if (i === 1) {
            const scaleValue = Math.round(twsScale(numVal));
            cellClass += ` r${Math.max(0, Math.min(6, scaleValue))}`;
          } else if (i === 3) {
            const scaleValue = Math.round(swhScale(numVal));
            cellClass += ` b${Math.max(0, Math.min(6, scaleValue))}`;
          } else if (i === 4) {
            const scaleValue = Math.round(distScale(numVal));
            cellClass += ` r${Math.max(0, Math.min(6, scaleValue))}`;
          }
          row.push({
            value,
            class: cellClass,
            isFirst: colIndex === 0
          });
          colIndex++;
        }
        rows.push(row);
      }

      return { headers, rows };
    } catch (err: unknown) {
      logError("Error parsing stats data:", err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  };

  // Format date string
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "";
    // If date is in YYYYMMDD format, convert to YYYY-MM-DD
    if (dateStr.length === 8 && !dateStr.includes('-')) {
      return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
    }
    return dateStr;
  };

  // Fetch all data on mount
  onMount(async () => {
    try {
      setLoading(true);
      setError(null);
      
      await logPageLoad('Notes.tsx', 'Dataset Notes Report');

      const classNameValue = selectedClassName();
      const projectId = selectedProjectId();
      const datasetId = selectedDatasetId();

      if (!classNameValue || !projectId || !datasetId) {
        setError("Missing required parameters: class_name, project_id, or dataset_id");
        setLoading(false);
        return;
      }

      setClassName(classNameValue);

      // Fetch dataset info for date and report name
      const controller = new AbortController();
      const datasetInfoResponse = await getData(
        `${apiEndpoints.app.datasets}/info?class_name=${encodeURIComponent(classNameValue)}&project_id=${encodeURIComponent(projectId)}&dataset_id=${encodeURIComponent(datasetId)}`,
        controller.signal
      );

      if (datasetInfoResponse.success && datasetInfoResponse.data) {
        setDatasetDate(formatDate(datasetInfoResponse.data.date || ""));
      }

      const datasetIdResponse = await getData(
        `${apiEndpoints.app.datasets}/id?class_name=${encodeURIComponent(classNameValue)}&project_id=${encodeURIComponent(projectId)}&dataset_id=${encodeURIComponent(datasetId)}`,
        controller.signal
      );

      if (datasetIdResponse.success && datasetIdResponse.data) {
        setReportName(datasetIdResponse.data.report_name || "");
        setDatasetDescription(String(datasetIdResponse.data.description || ""));
      }

      // Fetch all dataset objects in parallel
      const [
        introData,
        notesData,
        dayTypeNotesData,
        techniqueData,
        howToWinData,
        configData,
        crewData,
        maximumsData,
        statsData,
        computedSummaryData
      ] = await Promise.all([
        fetchDatasetObject('summary'),
        fetchDatasetObject('notes'),
        fetchDatasetObject('day_type_notes'),
        fetchDatasetObject('technique'),
        fetchDatasetObject('how_to_win'),
        fetchDatasetObject('configuration'),
        fetchDatasetObject('crew'),
        fetchDatasetObject('maximum'),
        fetchDatasetObject('stats'),
        fetchDatasetObject('computed_summary'),
      ]);

      setIntroduction(introData);
      setNotes(notesData);
      setDayTypeNotes(dayTypeNotesData);
      setTechnique(techniqueData);
      setHowToWin(howToWinData);
      setConfiguration(parseConfiguration(configData));
      setCrew(crewData as CrewRotation | CrewRotation[] | null);
      setMaximums(maximumsData as Record<string, string | number> | null);
      setStats(statsData);
      setComputedSummary(parseComputedSummary(computedSummaryData));

      // Placeholders and the dataset description are not notes.
      const descriptionText = datasetDescription();
      const hasIntro = isMeaningfulNoteSection(getText(introData), descriptionText);
      const hasNotes = isMeaningfulNoteSection(getText(notesData), descriptionText);
      const hasDayType = isMeaningfulNoteSection(getText(dayTypeNotesData), descriptionText) ||
                         isMeaningfulNoteSection(getText(techniqueData), descriptionText) ||
                         isMeaningfulNoteSection(getText(howToWinData), descriptionText);
      const hasConfig = parseConfiguration(configData) != null;
      const hasCrew = crewData != null;
      const hasMaximums = maximumsData != null;
      const hasStats = statsData != null;
      const hasSummary = parseComputedSummary(computedSummaryData) != null;

      setHasContent(hasIntro || hasNotes || hasDayType || hasConfig || hasCrew || hasMaximums || hasStats || hasSummary);

      const meaningfulNotes = notesHaveMeaningfulContent(
        [getText(introData), getText(notesData), getText(dayTypeNotesData), getText(techniqueData), getText(howToWinData)],
        descriptionText
      );
      if (!meaningfulNotes) {
        const removed = await deleteData(`${apiEndpoints.admin.datasets}/page`, {
          class_name: classNameValue,
          project_id: projectId,
          dataset_id: datasetId,
          page_name: "NOTES",
        });
        if (removed.success) {
          debug("[Notes] Removed NOTES page; description and empty notes do not count");
          setSidebarMenuRefreshTrigger((sidebarMenuRefreshTrigger() || 0) + 1);
        } else {
          warn("[Notes] Failed to remove NOTES page:", removed.message || "Unknown error");
        }
      }

    } catch (err: unknown) {
      logError("Error loading notes data:", err instanceof Error ? err : new Error(String(err)));
      setError(err instanceof Error ? err.message : "Failed to load notes data");
    } finally {
      setLoading(false);
    }
  });

  // Build coach notes HTML
  const buildCoachNotesHTML = () => {
    const dayTypeText = getText(dayTypeNotes());
    const techniqueText = getText(technique());
    const howToWinText = getText(howToWin());

    let html = "";

    if (isMeaningfulNoteSection(dayTypeText, datasetDescription())) {
      html += "<h2>Weather</h2>" + dayTypeText;
    }

    if (isMeaningfulNoteSection(techniqueText, datasetDescription())) {
      html += "<h2>Technique</h2>" + techniqueText;
    }

    if (isMeaningfulNoteSection(howToWinText, datasetDescription())) {
      html += "<h2>How To Win The Day</h2>" + howToWinText;
    }

    return html;
  };

  const statsTableData = () => buildStatsTableData(stats());
  const coachNotesHTML = () => buildCoachNotesHTML();

  return (
    <div class="notes-container">
      <Show when={loading()}>
        <Loading />
      </Show>

      <Show when={!loading() && error()}>
        <div class="oops" style="text-align: center; padding: 40px;">
          <h1 class="big">Error</h1>
          <p>{error()}</p>
        </div>
      </Show>

      <Show when={!loading() && !error() && !hasContent()}>
        <article class="oops" style="text-align: center; padding: 40px;">
          <h1 class="big">Hang in there!</h1>
          <p>Looks like we haven't got around to the notes yet...</p>
        </article>
      </Show>

      <Show when={!loading() && !error() && hasContent()}>
        {/* Introduction Section */}
        <Show when={isMeaningfulNoteSection(getText(introduction()), datasetDescription())}>
          <div id="introduction-section">
            <div id="introduction-title"><h2 class="left">Introduction</h2></div>
            <div 
              id="introduction"
              innerHTML={sanitizeHTML(getText(introduction()))}
            />
          </div>
        </Show>

        {/* Configuration (dataset_objects: parent_name=dataset, object_name=configuration) */}
        <Show when={configuration() != null}>
          <div id="configuration">
            <div id="configuration-title"><h2 class="left">Configuration</h2></div>
            <div class="data-table-container notes-summary-table-wrap">
              <table id="configuration_table" class="data-table notes-summary-race-table">
                <thead>
                  <tr>
                    <Show when={configuration()?.platform}>
                      <th>Platform</th>
                    </Show>
                    <th>Mast</th>
                    <th>Foil</th>
                    <th>Rudder</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <Show when={configuration()?.platform}>
                      <td>{configuration()?.platform}</td>
                    </Show>
                    <td>{configuration()?.mast || ""}</td>
                    <td>{configuration()?.foil || ""}</td>
                    <td>{configuration()?.rudder || ""}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Show>

        {/* Computed Day / Race Summary */}
        <Show when={computedSummary()}>
          {(summaryAccessor) => {
            const data = summaryAccessor()!;
            const day = data.day;
            return (
              <section class="notes-summary">
                <h2 class="notes-summary-heading">Day Summary</h2>

                <div class="notes-summary-hero-grid">
                  <div class="notes-summary-hero-card">
                    <span class="notes-summary-hero-label">Top Boat Speed</span>
                    <div class="notes-summary-hero-row">
                      <span class="notes-summary-hero-value notes-summary-hero-value-compact">{formatNumber(day.top_bsp_kts, 1)}</span>
                      <span class="notes-summary-hero-unit">kts</span>
                    </div>
                  </div>
                  <div class="notes-summary-hero-card">
                    <span class="notes-summary-hero-label">Total Distance</span>
                    <div class="notes-summary-hero-row">
                      <span class="notes-summary-hero-value notes-summary-hero-value-compact">{formatNumber(day.distance_nm, 1)}</span>
                      <span class="notes-summary-hero-unit">nm</span>
                    </div>
                  </div>
                  <div class="notes-summary-hero-card">
                    <span class="notes-summary-hero-label">Total Hours</span>
                    <div class="notes-summary-hero-row">
                      <span class="notes-summary-hero-value notes-summary-hero-value-compact">{formatTotalHours(dayTotalHoursSec(day))}</span>
                    </div>
                  </div>
                </div>

                <Show when={data.races.length > 0}>
                  <h3 class="notes-summary-subheading">Training &amp; Race Breakdown</h3>
                  <div class="data-table-container notes-summary-table-wrap">
                    <table class="data-table notes-summary-race-table">
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Duration</th>
                          <th>Top BSP</th>
                          <th>Avg TWS</th>
                          <th>Avg TWD</th>
                          <th>Distance</th>
                          <th>Tacks</th>
                          <th>Gybes</th>
                          <th>Roundups</th>
                          <th>Bearaways</th>
                          <th>Foiling</th>
                          <th>Upwind</th>
                          <th>Downwind</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={data.races}>
                          {(race) => (
                            <tr>
                              <td>{raceDisplayName(race)}</td>
                              <td>{formatDuration(raceDurationSec(race))}</td>
                              <td>{formatNumber(race.top_bsp_kts, 1)}</td>
                              <td>{formatNumber(race.tws_avg_kts, 1)}</td>
                              <td>{formatNumber(race.twd_avg_deg, 0)}</td>
                              <td>{formatNumber(race.distance_nm, 1)}</td>
                              <td>{race.maneuvers.tacks}</td>
                              <td>{race.maneuvers.gybes}</td>
                              <td>{race.maneuvers.roundups}</td>
                              <td>{race.maneuvers.bearaways}</td>
                              <td>{formatDuration(race.mode_sec.foiling)}</td>
                              <td>{formatDuration(race.point_of_sail_sec.upwind)}</td>
                              <td>{formatDuration(race.point_of_sail_sec.downwind)}</td>
                            </tr>
                          )}
                        </For>
                        <Show when={buildBreakdownTotals(data.races)}>
                          {(totalsAccessor) => {
                            const totals = totalsAccessor()!;
                            return (
                              <tr class="notes-summary-totals-row">
                                <td>Totals</td>
                                <td>{formatDuration(totals.durationSec)}</td>
                                <td>{formatNumber(totals.top_bsp_kts, 1)}</td>
                                <td>{formatNumber(totals.tws_avg_kts, 1)}</td>
                                <td>{formatNumber(totals.twd_avg_deg, 0)}</td>
                                <td>{formatNumber(totals.distance_nm, 1)}</td>
                                <td>{totals.maneuvers.tacks}</td>
                                <td>{totals.maneuvers.gybes}</td>
                                <td>{totals.maneuvers.roundups}</td>
                                <td>{totals.maneuvers.bearaways}</td>
                                <td>{formatDuration(totals.mode_sec.foiling)}</td>
                                <td>{formatDuration(totals.point_of_sail_sec.upwind)}</td>
                                <td>{formatDuration(totals.point_of_sail_sec.downwind)}</td>
                              </tr>
                            );
                          }}
                        </Show>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </section>
            );
          }}
        </Show>

        {/* Coach Notes: Weather, Technique, How to Win */}
        <Show when={coachNotesHTML().length > 0}>
          <div id="daytype-section">
            <div 
              id="daytype"
              innerHTML={sanitizeHTML(coachNotesHTML())}
            />
          </div>
        </Show>

        {/* Capture Notes Section */}
        <Show when={isMeaningfulNoteSection(getText(notes()), datasetDescription())}>
          <div id="notes-section">
            <div id="notes-title"><h2 class="left">Capture Notes</h2></div>
            <div 
              id="notes"
              innerHTML={sanitizeHTML(getText(notes()))}
            />
          </div>
        </Show>

        {/* Crew Table */}
        <Show when={crew() != null}>
          <div id="crew">
            <div id="crew-title"><h2 class="left">Crew</h2></div>
            <table id="crew_table" class="table table-bordered table-striped">
              <thead class="thead-dark">
                <tr>
                  <th class="head">Rotation</th>
                  <th class="head">Helmsman</th>
                  <th class="head">Wing Trim</th>
                  <th class="head">Flight</th>
                  <th class="head">Strategy</th>
                  <th class="head">Grind Aft</th>
                  <th class="head">Grind Fwd</th>
                </tr>
              </thead>
              <tbody>
                <Show when={!isArray(crew())}>
                  <tr>
                    <td class="centered">ROTATION 1</td>
                    <td class="centered">{(crew() as CrewRotation | null)?.helmsman ?? ""}</td>
                    <td class="centered">{(crew() as CrewRotation | null)?.wing_trim ?? ""}</td>
                    <td class="centered">{(crew() as CrewRotation | null)?.flight ?? ""}</td>
                    <td class="centered">{(crew() as CrewRotation | null)?.strategy ?? ""}</td>
                    <td class="centered">{(crew() as CrewRotation | null)?.grind_aft ?? ""}</td>
                    <td class="centered">{(crew() as CrewRotation | null)?.grind_fwd ?? ""}</td>
                  </tr>
                </Show>
                <Show when={isArray(crew())}>
                  <For each={((crew() as CrewRotation[] | null) ?? []).slice().reverse()}>
                    {(rotation: CrewRotation) => (
                      <tr>
                        <td class="centered">{rotation.name || ""}</td>
                        <td class="centered">{rotation.helmsman || ""}</td>
                        <td class="centered">{rotation.wing_trim || ""}</td>
                        <td class="centered">{rotation.flight || ""}</td>
                        <td class="centered">{rotation.strategy || ""}</td>
                        <td class="centered">{rotation.grind_aft || ""}</td>
                        <td class="centered">{rotation.grind_fwd || ""}</td>
                      </tr>
                    )}
                  </For>
                </Show>
              </tbody>
            </table>
          </div>
        </Show>

        {/* Summary Stats Table */}
        <Show when={statsTableData() != null}>
          <div id="stats">
            <div id="stats-title"><h2 class="left">Summary Stats</h2></div>
            <table id="stats_table" class="table table-bordered table-striped">
              <thead class="thead-dark">
                <tr>
                  <For each={statsTableData()?.headers || []}>
                    {(header) => <th class="head">{header}</th>}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={statsTableData()?.rows || []}>
                  {(row) => (
                    <tr>
                      <For each={row}>
                        {(cell) => (
                          <td class={cell.class}>{cell.value}</td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        {/* Daily Maximums Table */}
        <Show when={maximums() != null}>
          <div id="maximums">
            <div id="max-stats-title"><h2 class="left">Daily Maximums</h2></div>
            <table id="max-stats_table" class="table table-bordered table-striped">
              <thead class="thead-dark">
                <tr>
                  <th class="head">SPEED [KTS]</th>
                  <th class="head">G-FORCE</th>
                  <th class="head">TRIM [DEG]</th>
                  <th class="head">HEEL [DEG]</th>
                  <th class="head">HULL AIR TEMP [C]</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="centered">{maximums()?.bsp || ""}</td>
                  <td class="centered">{maximums()?.gforce || ""}</td>
                  <td class="centered">{maximums()?.trim || ""}</td>
                  <td class="centered">{maximums()?.heel || ""}</td>
                  <td class="centered">{maximums()?.air_temp || ""}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Show>

        {/* Daily Usage Table */}
        <Show when={usage() != null}>
          <div id="usage">
            <div id="usage-stats-title"><h2 class="left">Daily Usage</h2></div>
            <table id="usage-stats_table" class="table table-bordered table-striped">
              <thead class="thead-dark">
                <tr>
                  <th class="head">PORT FCS CYCLES *</th>
                  <th class="head">STBD FCS CYCLES *</th>
                  <th class="head">LOCK LOADED [H] **</th>
                  <th class="head">TRAVELER [M] ***</th>
                  <th class="head">JIB CAR [M] ***</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="centered">{usage()?.port_fcs || ""}</td>
                  <td class="centered">{usage()?.stbd_fcs || ""}</td>
                  <td class="centered">{usage()?.lock_hours || ""}</td>
                  <td class="centered">{usage()?.trav_m || ""}</td>
                  <td class="centered">{usage()?.jibcar_m || ""}</td>
                </tr>
              </tbody>
            </table>
            <p class="left">* An fcs cycle includes 1 drop and 1 raise</p>
            <p class="left">** Jib lock loaded hours is with a jib lock load greater than 11 ton</p>
            <p class="left">*** Traveler and jib car movement is with a jib lock load greater than 4 ton</p>
            <p class="left">**** Power related stats combine pump cadence with pressure, over a 1 second moving average to remove presure peaks, and are normalized to represent a single cyclor where boatspeed &gt; 10 knots and mainsheet load &gt; 1 ton.</p>
            <p class="left">***** Kcals are computed from average watts at the pump multiplied by seconds, with boatspeed &gt; 10 and mainsheet load &gt; 1 ton, normalized to a single cyclor.</p>
          </div>
        </Show>
      </Show>
    </div>
  );
}
